import React from 'react';
import { PageBanner } from '../components/PageBanner';
import { ContactCTA } from '../components/ContactCTA';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { WindIcon, ShieldCheckIcon, ActivityIcon, StethoscopeIcon, CheckCircleIcon, AlertCircleIcon } from 'lucide-react';
export function TracheostomyPage() {
  const {
    ref: introRef,
    isVisible: isIntroVisible
  } = useScrollAnimation();
  const {
    ref: benefitsRef,
    isVisible: isBenefitsVisible
  } = useScrollAnimation();
  const {
    ref: detailsRef,
    isVisible: isDetailsVisible
  } = useScrollAnimation();
  const {
    ref: ctaRef,
    isVisible: isCtaVisible
  } = useScrollAnimation();
  return <main data-id="element-1267">
      <PageBanner title="Tracheostomy (Trac) Care" subtitle="Specialized respiratory support for long-term airway management." breadcrumbs={[{
      label: 'Services',
      path: '/services'
    }, {
      label: 'Tracheostomy Care',
      path: '/services/tracheostomy'
    }]} data-id="element-1268" />

      {/* Intro Section */}
      <section className="py-16 md:py-20 bg-white" data-id="element-1269">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1270">
          <div ref={introRef} className={`transition-all duration-1000 ease-out ${isIntroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} data-id="element-1271">
            <div className="inline-flex items-center space-x-2 bg-primary/10 rounded-full px-4 py-2 mb-6" data-id="element-1272">
              <WindIcon className="h-4 w-4 text-primary" data-id="element-1273" />
              <span className="text-primary text-sm font-semibold" data-id="element-1274">
                Respiratory Support
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-dark mb-6 leading-tight" data-id="element-1275">
              Expert Airway Management at Home
            </h2>
            <div className="space-y-5 text-dark-muted text-lg leading-relaxed" data-id="element-1276">
              <p data-id="element-1277">
                Specialized respiratory support for patients requiring long-term
                airway management. This includes regular suctioning, stoma care,
                and monitoring to ensure optimal oxygenation and prevent
                respiratory complications.
              </p>
            </div>

            {/* RN Badge */}
            <div className="mt-8 flex items-center bg-primary/5 border border-primary/10 rounded-xl p-5" data-id="element-1278">
              <ShieldCheckIcon className="h-10 w-10 text-primary mr-4 flex-shrink-0" data-id="element-1279" />
              <div data-id="element-1280">
                <h4 className="font-bold text-dark" data-id="element-1281">RN Supervised Care</h4>
                <p className="text-sm text-dark-muted" data-id="element-1282">
                  All tracheostomy care is managed by skilled nurses trained in
                  advanced respiratory protocols.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 md:py-20 bg-light-alt" data-id="element-1283">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1284">
          <div ref={benefitsRef} className={`transition-all duration-1000 ease-out ${isBenefitsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} data-id="element-1285">
            <div className="text-center mb-14" data-id="element-1286">
              <h2 className="text-3xl md:text-4xl font-bold text-dark mb-4" data-id="element-1287">
                Comprehensive Tracheostomy Support
              </h2>
              <div className="w-16 h-1.5 bg-secondary mx-auto mb-6 rounded-full" data-id="element-1288"></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-1289">
              {[{
              icon: WindIcon,
              title: 'Airway Management',
              desc: 'Ensuring clear and patent airways for optimal breathing and comfort.'
            }, {
              icon: ActivityIcon,
              title: 'Regular Suctioning',
              desc: 'Safe and sterile suctioning techniques to clear secretions effectively.'
            }, {
              icon: ShieldCheckIcon,
              title: 'Stoma Care',
              desc: 'Meticulous cleaning and dressing of the stoma site to prevent infection.'
            }, {
              icon: StethoscopeIcon,
              title: 'Respiratory Monitoring',
              desc: 'Continuous assessment of oxygenation and respiratory status.'
            }].map((benefit, idx) => <div key={idx} className="bg-white rounded-2xl p-7 shadow-card hover:shadow-card-hover border border-gray-100 transition-all duration-300 transform hover:-translate-y-1 group" data-id="element-1290">
                  <div className="bg-primary/10 w-14 h-14 rounded-xl flex items-center justify-center mb-5 group-hover:bg-primary transition-colors duration-300" data-id="element-1291">
                    <benefit.icon className="h-7 w-7 text-primary group-hover:text-white transition-colors duration-300" data-id="element-1292" />
                  </div>
                  <h3 className="text-lg font-bold text-dark mb-3" data-id="element-1293">
                    {benefit.title}
                  </h3>
                  <p className="text-dark-muted text-sm leading-relaxed" data-id="element-1294">
                    {benefit.desc}
                  </p>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* Details Section */}
      <section className="py-16 md:py-20 bg-white" data-id="element-1295">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1296">
          <div ref={detailsRef} className={`transition-all duration-1000 ease-out ${isDetailsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} data-id="element-1297">
            <h2 className="text-3xl font-bold text-dark mb-6" data-id="element-1298">
              Our Tracheostomy Services Include
            </h2>
            <p className="text-dark-muted text-lg leading-relaxed mb-8" data-id="element-1299">
              We provide comprehensive care to maintain airway patency, prevent
              complications, and ensure the highest quality of life for patients
              with tracheostomies.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8" data-id="element-1300">
              {['Regular tracheostomy suctioning', 'Stoma site cleaning & care', 'Oxygenation monitoring', 'Emergency preparedness', 'Respiratory complication prevention', 'Inner cannula maintenance'].map((item, idx) => <div key={idx} className="flex items-center p-4 bg-light-alt rounded-xl border border-gray-100" data-id="element-1301">
                  <CheckCircleIcon className="h-6 w-6 text-secondary mr-3 flex-shrink-0" data-id="element-1302" />
                  <span className="text-dark font-medium" data-id="element-1303">{item}</span>
                </div>)}
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 flex items-start" data-id="element-1304">
              <AlertCircleIcon className="h-6 w-6 text-amber-500 mr-3 flex-shrink-0" data-id="element-1305" />
              <p className="text-amber-800 text-sm font-medium pt-0.5" data-id="element-1306">
                Our team is fully trained in emergency tracheostomy protocols
                and maintains strict safety standards at all times.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-16 bg-light-alt" data-id="element-1307">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1308">
          <div ref={ctaRef} className={`bg-primary rounded-3xl p-10 md:p-14 text-center shadow-xl relative overflow-hidden transition-all duration-1000 ease-out ${isCtaVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-12 scale-95'}`} data-id="element-1309">
            <div className="absolute inset-0 opacity-10" style={{
            backgroundImage: 'radial-gradient(#ffffff 2px, transparent 2px)',
            backgroundSize: '30px 30px'
          }} data-id="element-1310"></div>
            <div className="relative z-10" data-id="element-1311">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{
              fontFamily: "'Cinzel', serif"
            }} data-id="element-1312">
                Need Specialized Airway Care?
              </h2>
              <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto" data-id="element-1313">
                Contact us today to discuss your respiratory care needs and
                schedule a consultation with our nursing team.
              </p>
              <a href="#/contact" className="inline-block bg-white text-primary-dark hover:bg-light-alt px-10 py-4 rounded-xl font-bold text-lg transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1" data-id="element-1314">
                Contact Us Today
              </a>
            </div>
          </div>
        </div>
      </section>

      <ContactCTA data-id="element-1315" />
    </main>;
}